import { Button, DatePicker, Form, Input, Upload } from 'antd';
import * as eva from 'eva-icons';
import moment from 'moment';
import momentGenerateConfig from 'rc-picker/lib/generate/moment';
import React, { useEffect } from 'react';
import {
  DEFAULTDATEFORMAT,
  FALLBACK_PDF_URL,
  FIELD_TYPES,
} from '../../../../../../common/constants';
import {
  formatNumericInput,
  formValidatorRules,
} from '../../../../../../common/utils';
import CommonSelect from '../../../../../../components/CommonSelect';
import FieldIcon from './FieldIcon';

const MyDatePicker = DatePicker.generatePicker(momentGenerateConfig);

const UploadComponent = React.memo(
  ({
    uploadFieldsName,
    record,
    dataIndex,
    fileListRef,
    fieldDisabled,
    handleUploadChange,
    onUploadRemove,
    openFileDialogOnClick,
    uploadKey,
    beforeUpload,
    attachmentRef,
    onFieldClick,
  }) => {
    if (!fileListRef?.current?.[uploadFieldsName]) {
      const initialFiles = Array.isArray(record?.[dataIndex])
        ? record[dataIndex]?.slice(-3)?.map((item, idx) => ({
            uid: `${uploadFieldsName}-${idx}`,
            status: 'done',
            url: item?.value || item?.url,
            key: item?.key,
            name:
              item?.name ||
              (item?.key ? item?.key?.split('/')?.pop() : undefined),
            thumbUrl:
              item?.key?.split('.')?.[item?.key?.split('.')?.length - 1] ===
              'pdf'
                ? FALLBACK_PDF_URL
                : item?.value || item?.url,
          }))
        : [];
      // eslint-disable-next-line no-param-reassign
      fileListRef.current[uploadFieldsName] = initialFiles;
    } else {
      // eslint-disable-next-line no-param-reassign
      fileListRef.current[uploadFieldsName] =
        fileListRef?.current[uploadFieldsName]?.slice(-3);
    }

    const handlePreview = async (file) => {
      let previewUrl = file?.url;
      if (file?.originFileObj) {
        previewUrl = URL.createObjectURL(file?.originFileObj);
      } else if (file?.thumbUrl && !file?.url) {
        previewUrl = file?.thumbUrl;
      }

      if (previewUrl) {
        // eslint-disable-next-line no-undef
        window?.open(previewUrl, '_blank');
        if (file?.originFileObj) {
          URL.revokeObjectURL(previewUrl);
        }
      }
    };

    return (
      <Upload
        disabled={fieldDisabled}
        openFileDialogOnClick={openFileDialogOnClick}
        key={uploadKey}
        maxCount={3}
        accept=".png,.jpg,.jpeg,.pdf"
        multiple
        listType="picture-card"
        className="register-attachment"
        fileList={fileListRef?.current?.[uploadFieldsName]?.slice(-3)}
        showUploadList={{
          showPreviewIcon: true,
          showRemoveIcon: true,
          showDownloadIcon: false,
        }}
        beforeUpload={(file) => beforeUpload(file)}
        onPreview={handlePreview}
        onChange={(info) => handleUploadChange(info)}
        onRemove={onUploadRemove}
      >
        {(!fileListRef?.current[uploadFieldsName] ||
          fileListRef?.current[uploadFieldsName]?.length < 3) && (
          <div className="height-percent-100 width-percent-100 d-flex align-center justify-center pointer">
            <Button
              tabIndex={0}
              className="upload-attachment-button"
              type="primary"
              shape="round"
              ref={attachmentRef}
              onClick={onFieldClick}
              disabled={fieldDisabled}
            >
              Upload
            </Button>
          </div>
        )}
      </Upload>
    );
  },
);

const RenderFields = ({
  fieldType,
  fieldIndex,
  fieldName,
  initialFieldValue,
  fieldIsRequired,
  formClassName = '',
  fieldDisabled = false,
  divClassName = '',
  onFieldClick = () => {},
  onTextChange = () => {},
  onTextBlur = () => {},
  onNumericChange = () => {},
  onNumericBlur = () => {},
  onOptionChange = () => {},
  onOptionDropdownVisibilityChange = () => {},
  optionsOpen = {},
  onOptionsFocus = () => {},
  options,
  onOptionOpenChange = () => {},
  onOptionBlur = () => {},
  popupClassName = {},
  multiSelectOpen = {},
  onMultiSelectFocus = () => {},
  onMultiSelectBlur = () => {},
  onMultiSelectDropdownVisibilityChange = () => {},
  onMultiSelectChange = () => {},
  getPopupContainer = () => {},
  dateOpen = {},
  onDateFocused = {},
  onDateOpenChange = () => {},
  onDateChange = () => {},
  onDateBlur = () => {},
  datePopupClassName = '',
  onCalculationChange = () => {},
  onCalculationBlur = () => {},
  calculateFieldValue = () => {},
  openFileDialogOnClick = false,
  fileListRef = {},
  records = {},
  uploadDataIndex = '',
  uploadFieldName = '',
  handleUploadChange = () => {},
  inputRef = null,
  multiLineRef = null,
  numericRef = null,
  optionsRef = null,
  multiSelectRef = null,
  dateRef = null,
  attachmentRef = null,
  uploadKey = '',
  beforeUpload = () => {},
  onUploadRemove = () => {},
}) => {
  const { characterWithoutWhiteSpace } = formValidatorRules;

  useEffect(() => {
    eva.replace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  switch (fieldType) {
    case FIELD_TYPES.TEXT:
      return (
        <Form.Item
          name={fieldIndex}
          className={formClassName}
          initialValue={initialFieldValue}
          label={
            fieldName && (
              <span className="d-flex align-center white-space-pre-line text-break">
                <div className="mr-5">
                  <FieldIcon fieldType={fieldType} />
                </div>
                {fieldName}
              </span>
            )
          }
          rules={[
            {
              required: fieldIsRequired,
              message: 'This field is required!',
            },
            characterWithoutWhiteSpace,
            {
              max: 250,
              message: 'Text cannot be more than 250 characters',
            },
          ]}
        >
          <Input
            placeholder="Enter text"
            ref={inputRef}
            disabled={fieldDisabled}
            onChange={onTextChange}
            onClick={onFieldClick}
            onBlur={onTextBlur}
          />
        </Form.Item>
      );

    case FIELD_TYPES.MULTI_LINE_TEXT:
      return (
        <Form.Item
          name={fieldIndex}
          className={formClassName}
          initialValue={initialFieldValue}
          label={
            fieldName && (
              <span className="d-flex align-center white-space-pre-line text-break">
                <div className="mr-5">
                  <FieldIcon fieldType={fieldType} />
                </div>
                {fieldName}
              </span>
            )
          }
          rules={[
            {
              required: fieldIsRequired,
              message: 'This field is required!',
            },
            {
              max: 350,
              message: 'Maximum 350 characters are allowed',
            },
          ]}
        >
          <Input.TextArea
            placeholder="Enter text"
            rows={4}
            ref={multiLineRef}
            disabled={fieldDisabled}
            onChange={onTextChange}
            onClick={onFieldClick}
            onBlur={onTextBlur}
          />
        </Form.Item>
      );

    case FIELD_TYPES.NUMERIC:
      return (
        <Form.Item
          name={fieldIndex}
          className={formClassName}
          initialValue={initialFieldValue}
          label={
            fieldName && (
              <span className="d-flex align-center white-space-pre-line text-break">
                <div className="mr-5">
                  <FieldIcon fieldType={fieldType} />
                </div>
                {fieldName}
              </span>
            )
          }
          rules={[
            {
              required: fieldIsRequired,
              message: 'This field is required!',
            },
            {
              max: 15,
              message: 'Maximum 15 digits are allowed',
            },
            {
              validator: (_, value) => {
                if (!value || value === '') {
                  return Promise?.resolve();
                }
                const numericValue = Number(value);
                if (Number?.isNaN(numericValue)) {
                  return Promise?.reject(
                    new Error('Please enter a valid number'),
                  );
                }
                return Promise?.resolve();
              },
            },
          ]}
          normalize={(value) => formatNumericInput(value)}
        >
          <Input
            placeholder="Enter number"
            inputMode="decimal"
            disabled={fieldDisabled}
            ref={numericRef}
            onChange={onNumericChange}
            onBlur={onNumericBlur}
            onClick={onFieldClick}
          />
        </Form.Item>
      );

    case FIELD_TYPES.OPTIONS:
      return (
        <Form.Item
          name={fieldIndex}
          className={formClassName}
          initialValue={initialFieldValue || undefined}
          label={
            fieldName && (
              <span className="d-flex align-center white-space-pre-line text-break">
                <div className="mr-5">
                  <FieldIcon fieldType={fieldType} />
                </div>
                {fieldName}
              </span>
            )
          }
          rules={[
            {
              required: fieldIsRequired,
              message: 'This field is required!',
            },
          ]}
        >
          <CommonSelect
            placeholder="Select Option"
            tabIndex={0}
            disabled={fieldDisabled}
            name={fieldName}
            open={optionsOpen}
            inputRef={optionsRef}
            onFocus={onOptionsFocus}
            onChange={onOptionChange}
            options={options}
            onOpenChange={onOptionOpenChange}
            onBlur={onOptionBlur}
            onDropdownVisibleChange={onOptionDropdownVisibilityChange}
            allowClear
            showSearch
            popupClassName={popupClassName}
            onClick={onFieldClick}
            getPopupContainer={getPopupContainer}
          />
        </Form.Item>
      );

    case FIELD_TYPES.CHECKBOXES:
      return (
        <Form.Item
          name={fieldIndex}
          className={formClassName}
          initialValue={initialFieldValue}
          label={
            fieldName && (
              <span className="d-flex align-center white-space-pre-line text-break">
                <div className="mr-5">
                  <FieldIcon fieldType={fieldType} />
                </div>
                {fieldName}
              </span>
            )
          }
          rules={[
            {
              required: fieldIsRequired,
              message: 'This field is required!',
            },
          ]}
        >
          <CommonSelect
            mode="multiple"
            placeholder="Select Options"
            tabIndex={0}
            disabled={fieldDisabled}
            name={fieldName}
            open={multiSelectOpen}
            inputRef={multiSelectRef}
            onFocus={onMultiSelectFocus}
            onBlur={onMultiSelectBlur}
            onDropdownVisibleChange={onMultiSelectDropdownVisibilityChange}
            onChange={onMultiSelectChange}
            options={options}
            allowClear
            showSearch
            popupClassName={popupClassName}
            onClick={onFieldClick}
            getPopupContainer={getPopupContainer}
          />
        </Form.Item>
      );

    case FIELD_TYPES.DATE:
      return (
        <Form.Item
          name={fieldIndex}
          className={formClassName}
          initialValue={
            initialFieldValue && moment(initialFieldValue, DEFAULTDATEFORMAT)
          }
          label={
            fieldName && (
              <span className="d-flex align-center white-space-pre-line text-break">
                <div className="mr-5">
                  <FieldIcon fieldType={fieldType} />
                </div>
                {fieldName}
              </span>
            )
          }
          rules={[
            {
              required: fieldIsRequired,
              message: 'This field is required!',
            },
          ]}
        >
          <MyDatePicker
            open={dateOpen}
            onFocus={onDateFocused}
            onOpenChange={onDateOpenChange}
            onChange={onDateChange}
            ref={dateRef}
            className="width-percent-100 register-date-picker width-200"
            disabled={fieldDisabled}
            readOnly
            onClick={onFieldClick}
            suffixIcon={<i data-eva="calendar-outline" />}
            format={DEFAULTDATEFORMAT}
            popupClassName={datePopupClassName}
            onBlur={onDateBlur}
          />
        </Form.Item>
      );

    case FIELD_TYPES.ATTACHMENT:
      return (
        <div className={divClassName}>
          <Form.Item
            name={fieldIndex}
            label={
              fieldName && (
                <span className="d-flex align-center white-space-pre-line text-break">
                  <div className="mr-5">
                    <FieldIcon fieldType={fieldType} />
                  </div>
                  {fieldName}
                </span>
              )
            }
            initialValue={initialFieldValue}
            rules={[
              {
                required: fieldIsRequired,
                message: 'This field is required!',
              },
            ]}
          >
            <UploadComponent
              uploadFieldsName={uploadFieldName}
              record={records}
              dataIndex={uploadDataIndex}
              fileListRef={fileListRef}
              fieldDisabled={fieldDisabled}
              handleUploadChange={handleUploadChange}
              onUploadRemove={onUploadRemove}
              openFileDialogOnClick={openFileDialogOnClick}
              uploadKey={uploadKey}
              beforeUpload={beforeUpload}
              attachmentRef={attachmentRef}
              onFieldClick={onFieldClick}
            />
          </Form.Item>
        </div>
      );

    case FIELD_TYPES.CALCULATION: {
      const calculatedValue = calculateFieldValue();
      return (
        <Form.Item
          name={fieldIndex}
          className={formClassName}
          initialValue={calculatedValue || initialFieldValue || '-'}
          label={
            fieldName && (
              <span className="d-flex align-center white-space-pre-line text-break">
                <div className="mr-5">
                  <FieldIcon fieldType={fieldType} />
                </div>
                {fieldName}
              </span>
            )
          }
          rules={[
            {
              required: fieldIsRequired,
              message: 'This field is required!',
            },
          ]}
        >
          <Input
            disabled
            placeholder="-"
            value={calculatedValue || initialFieldValue || '-'}
            onChange={(e) => onCalculationChange(e)}
            onBlur={onCalculationBlur}
          />
        </Form.Item>
      );
    }

    default:
  }
};

export default React.memo(RenderFields);
